import { Banner, Button, Spinner } from '@augmentt-dev/ui-components'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import useAllEmployees from '../../../../../../hooks/useAllEmployees'
import { MAILBOX_DELEGATION_RIGHTS } from '../constants'
import styles from '../MailboxDelegations.module.scss'
import { MAILBOX_DELEGATION_ERROR_CODES } from '../useMailboxDelegations'
import DelegationRightEditor from './DelegationRightEditor/DelegationRightEditor'

const LOAD_ERROR_MESSAGES = {
  [MAILBOX_DELEGATION_ERROR_CODES.CONSENT_REQUIRED]: {
    id: 'ENGAGE.MAILBOX_DELEGATIONS.ERROR_CONSENT',
    defaultMessage: 'Mailbox permissions could not be loaded. Exchange admin consent is missing for this tenant.',
  },
  [MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE]: {
    id: 'ENGAGE.MAILBOX_DELEGATIONS.ERROR_UNAVAILABLE',
    defaultMessage: 'Mailbox permissions could not be loaded because Exchange could not be reached.',
  },
}

const SAVE_ERROR_MESSAGES = {
  [MAILBOX_DELEGATION_ERROR_CODES.CONSENT_REQUIRED]: {
    id: 'ENGAGE.MAILBOX_DELEGATIONS.ERROR_SAVE_CONSENT',
    defaultMessage: 'Mailbox permissions could not be changed. Exchange admin consent is missing for this tenant.',
  },
  [MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE]: {
    id: 'ENGAGE.MAILBOX_DELEGATIONS.ERROR_SAVE_UNAVAILABLE',
    defaultMessage: 'Mailbox permissions could not be changed because Exchange could not be reached.',
  },
  [MAILBOX_DELEGATION_ERROR_CODES.TRUSTEE_NO_MAILBOX]: {
    id: 'ENGAGE.MAILBOX_DELEGATIONS.ERROR_SAVE_NO_MAILBOX',
    defaultMessage: "This user can't be granted {right} because they have no mailbox.",
  },
}

const trusteeShape = PropTypes.shape({
  displayName: PropTypes.string,
  mail: PropTypes.string,
})

const propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  employeeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  delegations: PropTypes.shape({
    readAndManage: PropTypes.arrayOf(trusteeShape),
    sendAs: PropTypes.arrayOf(trusteeShape),
    sendOnBehalf: PropTypes.arrayOf(trusteeShape),
  }),
  loadStatus: PropTypes.oneOf(['idle', 'loading', 'ready', 'error']),
  loadErrorCode: PropTypes.string,
  pending: PropTypes.bool,
  refreshing: PropTypes.bool,
  refreshErrorCode: PropTypes.string,
  onRetry: PropTypes.func,
  onChange: PropTypes.func,
}

function ManageMailboxDelegationsModal({
  show = false,
  onHide = () => {},
  customerId = null,
  employeeId = null,
  delegations = null,
  loadStatus = 'ready',
  loadErrorCode = null,
  pending = false,
  refreshing = false,
  refreshErrorCode = null,
  onRetry = () => {},
  onChange = async () => ({ ok: false }),
}) {
  const intl = useIntl()
  const titleId = useId()
  const [selections, setSelections] = useState({})
  const [saveError, setSaveError] = useState(null)
  const { allEmployees, isFetching, fetchError, retry } = useAllEmployees({ customerId, show })

  useEffect(() => {
    if (!show) {
      setSaveError(null)
      setSelections({})
    }
  }, [show])

  const candidates = useMemo(
    () =>
      allEmployees.filter(
        (employee) => !employee.ignored && employee.email && String(employee.id) !== String(employeeId),
      ),
    [allEmployees, employeeId],
  )

  const dismissSaveError = useCallback(() => {
    setSaveError(null)
  }, [])

  const handleSelect = useCallback((rightKey, option) => {
    setSelections((current) => ({ ...current, [rightKey]: option }))
  }, [])

  const handleSubmit = useCallback(
    async (change, rightKey) => {
      const result = await onChange(change)
      if (!result?.ok) {
        setSaveError({
          ...(result ?? { errorCode: MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE }),
          right: rightKey,
        })
        return
      }
      setSaveError(null)
      setSelections((current) => ({ ...current, [rightKey]: null }))
    },
    [onChange],
  )

  const isDelegationLoading = loadStatus === 'loading' || loadStatus === 'idle'
  const isDelegationError = loadStatus === 'error'
  const loadErrorMessage =
    LOAD_ERROR_MESSAGES[loadErrorCode] ?? LOAD_ERROR_MESSAGES[MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE]
  const editsLocked = pending || refreshing || Boolean(refreshErrorCode) || isDelegationLoading || isDelegationError
  const saveErrorMessage = SAVE_ERROR_MESSAGES[saveError?.errorCode]
  const saveErrorRight = MAILBOX_DELEGATION_RIGHTS.find((entry) => entry.key === saveError?.right)
  const saveErrorValues =
    saveError?.errorCode === MAILBOX_DELEGATION_ERROR_CODES.TRUSTEE_NO_MAILBOX
      ? {
          right: intl.formatMessage({
            id: saveErrorRight?.messageId ?? 'ENGAGE.MAILBOX_DELEGATIONS.TITLE',
            defaultMessage: saveErrorRight?.defaultMessage ?? 'Mailbox permissions',
          }),
        }
      : undefined
  const saveErrorText = saveErrorMessage
    ? intl.formatMessage({ id: saveErrorMessage.id, defaultMessage: saveErrorMessage.defaultMessage }, saveErrorValues)
    : saveError?.message ||
      intl.formatMessage({
        id: SAVE_ERROR_MESSAGES[MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE].id,
        defaultMessage: SAVE_ERROR_MESSAGES[MAILBOX_DELEGATION_ERROR_CODES.EXCHANGE_UNAVAILABLE].defaultMessage,
      })

  return (
    <Modal
      className={styles.dialogScroll}
      show={show}
      onHide={onHide}
      centered
      enforceFocus={false}
      aria-labelledby={titleId}
    >
      <Modal.Header closeButton>
        <Modal.Title id={titleId}>
          <FormattedMessage id="ENGAGE.MAILBOX_DELEGATIONS.MANAGE_TITLE" defaultMessage="Manage mailbox permissions" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body-sidebar">
        {saveError && (
          <div className="mb-3">
            <Banner
              message={saveErrorText}
              buttonText={<FormattedMessage id="GENERAL.DISMISS" />}
              onPress={dismissSaveError}
            />
          </div>
        )}
        {refreshing && (
          <div className="d-flex align-items-center mb-3">
            <Spinner size="small" />
            <span className="visually-hidden">
              <FormattedMessage
                id="ENGAGE.MAILBOX_DELEGATIONS.REFRESHING"
                defaultMessage="Reloading mailbox permissions"
              />
            </span>
          </div>
        )}
        {refreshErrorCode && !refreshing && (
          <div className="mb-3">
            <Banner
              message={
                <FormattedMessage
                  id="ENGAGE.MAILBOX_DELEGATIONS.ERROR_REFRESH"
                  defaultMessage="The change was saved, but mailbox permissions could not be reloaded."
                />
              }
              buttonText={<FormattedMessage id="GENERAL.TRY_AGAIN" />}
              onPress={onRetry}
            />
          </div>
        )}
        {isDelegationLoading && (
          <div className="d-flex align-items-center mb-3">
            <Spinner size="small" />
            <span className="visually-hidden">
              <FormattedMessage id="ENGAGE.MAILBOX_DELEGATIONS.LOADING" defaultMessage="Loading mailbox permissions" />
            </span>
          </div>
        )}
        {isDelegationError && (
          <div className="mb-3">
            <Banner
              message={<FormattedMessage id={loadErrorMessage.id} defaultMessage={loadErrorMessage.defaultMessage} />}
              buttonText={<FormattedMessage id="GENERAL.TRY_AGAIN" />}
              onPress={onRetry}
            />
          </div>
        )}
        {fetchError && !isFetching && !isDelegationLoading && !isDelegationError && (
          <div className="mb-3">
            <Banner
              message={
                <FormattedMessage
                  id="ENGAGE.MAILBOX_DELEGATIONS.ERROR_EMPLOYEES"
                  defaultMessage="Users could not be loaded."
                />
              }
              buttonText={<FormattedMessage id="GENERAL.TRY_AGAIN" />}
              onPress={retry}
            />
          </div>
        )}

        {!isDelegationLoading && !isDelegationError && isFetching ? <Spinner size="small" /> : null}
        {!isDelegationLoading &&
          !isDelegationError &&
          !isFetching &&
          MAILBOX_DELEGATION_RIGHTS.map((right) => (
            <DelegationRightEditor
              key={right.key}
              right={right}
              trustees={delegations?.[right.key] ?? []}
              candidates={candidates}
              pending={editsLocked}
              selected={selections[right.key] ?? null}
              onSelect={handleSelect}
              onSubmit={handleSubmit}
            />
          ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={pending}>
          <FormattedMessage id="GENERAL.CLOSE" />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

ManageMailboxDelegationsModal.propTypes = propTypes

export default React.memo(ManageMailboxDelegationsModal)
